/**
 * Category landing page configs — one entry per `/wine`, `/comedy`,
 * `/brunch`, `/holiday` page. Drives the generic `CategoryLandingPage`
 * component so the per-category page files stay 5-line wrappers.
 *
 * Each config defines:
 *   slug         — URL slug and category match value
 *   label/plural — display names
 *   Icon         — Lucide icon for the hero eyebrow + empty-state
 *   accent       — Tailwind class strings (full literals so JIT picks
 *                  them up). Keep classes here, never compose
 *                  dynamically with template-literal slugs.
 *   heroImage    — Unsplash URL pre-sized with ?fm=webp&q=70&w=1600
 *   heroEyebrow  — eyebrow line in national mode
 *   heroTagline  — { lead, highlight, trail } that builds the gradient h1
 *   heroSubtitle — { city: ({cityName})=>string, national: string }
 *   badges       — 3 short tag-style badges with their own Icons
 *   seoTitle/seoDescription/seoKeywords — pure functions
 *   seoCopy      — long-form ReactNode for the bottom SEO block
 *   organizerNoun — text used by the bottom Organizer CTA
 */
import {
  Wine, Grape, Sparkles, Mic2, Laugh, Drama,
  Coffee, Utensils, Egg, Gift, PartyPopper, Snowflake,
  Ghost, Skull, Moon, Star, Music2, Flame,
} from 'lucide-react';
import React from 'react';

// ---------------------------------------------------------------------------
// Wine — burgundy / amber
// ---------------------------------------------------------------------------
export const wineConfig = {
  slug: 'wine',
  label: 'Wine',
  plural: 'Wine Events',
  organizerNoun: 'wine event',
  Icon: Wine,
  heroImage: 'https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?fm=webp&q=70&w=1600',
  heroEyebrow: 'The Eventism Wine Hub',
  heroTagline: { lead: 'Wine, but', highlight: 'worth showing up for', trail: '.' },
  heroSubtitle: {
    national: 'A curated calendar of vineyard dinners, sommelier-led tastings, varietal showcases and full-blown wine festivals — across every city we cover.',
    city: ({ cityName }) => `Vineyard dinners, sommelier-led tastings, and wine festivals across ${cityName}. Curated by Eventism, ticketed in one click.`,
  },
  badges: [
    { icon: Grape,    label: 'Vineyard dinners' },
    { icon: Sparkles, label: 'Sommelier nights' },
    { icon: Wine,     label: 'Wine festivals' },
  ],
  accent: {
    heroOverlay:    'bg-gradient-to-br from-[#3b0f1f]/95 via-[#1a0610]/90 to-[#2a1810]/95',
    titleGradient:  'from-rose-300 via-amber-300 to-rose-200',
    btnGradient:    'bg-gradient-to-r from-rose-500 to-amber-500',
    btnShadow:      'shadow-rose-500/20',
    btnOutline:     'border-rose-400/40 hover:bg-rose-500/10',
    ctaBg:          'bg-gradient-to-br from-amber-950/30 via-background to-rose-950/30',
    eyebrowColor:   'text-amber-300',
    linkText:       'text-rose-300',
    cardBorder:     'border-rose-400/20',
    hoverBorder:    'hover:border-rose-400/50',
    hoverBorderAlt: 'hover:border-amber-400/50',
    hoverBg:        'hover:bg-rose-500/5',
    badgeBorder:    'border-rose-400/30',
    badgeText:      'text-rose-200',
    badgeBg:        'bg-rose-500/10',
    badgePrimary:   'bg-rose-500/90',
    badgeSecondary: 'bg-amber-500/90 text-black',
    cardImageBg:    'bg-gradient-to-br from-rose-900/30 via-amber-900/20 to-rose-950/40',
    fallbackIcon:   'text-rose-300/40',
    statIconA:      'text-amber-300',
    statIconB:      'text-rose-300',
    priceText:      'text-amber-300',
    formActiveBorder: 'border-rose-400 bg-rose-500/10',
    formHoverBorder:  'hover:border-rose-400/40',
  },
  seoTitle: ({ isCityMode, cityName }) => isCityMode
    ? `Wine Events in ${cityName} — Tastings, Festivals & Vineyard Nights | Eventism`
    : `Wine Events — Tastings, Festivals & Vineyard Nights in Every City | Eventism`,
  seoDescription: ({ isCityMode, cityName, eventCount }) => isCityMode
    ? `${eventCount || 'Curated'} wine events in ${cityName}: tastings, vineyard dinners, sommelier nights, wine festivals. Tickets & guest lists, instant digital delivery via Eventism.`
    : `The Eventism Wine Hub: tastings, vineyard dinners, sommelier nights and wine festivals across Denver, LA, Vegas and Charlotte. Tickets & guest lists in one place.`,
  seoKeywords: ({ isCityMode, cityName }) => isCityMode
    ? [`wine events ${cityName}`, `${cityName} wine tasting`, `${cityName} vineyard events`, `${cityName} sommelier dinner`, `${cityName} wine festival`, `things to do ${cityName} wine`]
    : ['wine events', 'wine tasting events', 'vineyard dinners', 'sommelier nights', 'wine festivals', 'wine country experiences'],
  seoCopy: {
    cityTitle: ({ cityName }) => `The ${cityName} wine scene`,
    nationalTitle: 'Why curate a wine calendar?',
    city: ({ cityName, city, slug }) => (
      <>
        <p>{cityName} drinks like a city that&apos;s paying attention. The best pours aren&apos;t always at the splashiest restaurants — they live at the vineyard dinners, the sommelier-led flights tucked above a wine bar, and the once-a-year festivals where ten producers fly in for a single night.</p>
        <p>Eventism collects them in one place. Every event on this page is independently programmed by an organizer we vetted, ticketed end-to-end so you walk in with a digital pass and nothing else to think about.</p>
        <p>Looking for something specific? Bookmark <a href={`/${slug}/${city}`} className="text-rose-300 hover:underline">/{slug}/{city}</a> — we add new events weekly.</p>
      </>
    ),
    national: () => (
      <>
        <p>Wine events used to be the hardest category to find. They live in private cellars, vineyard barns, dimly lit upstairs rooms above the city&apos;s best restaurants. They rarely make Eventbrite. They rarely make Google.</p>
        <p>The Eventism Wine Hub fixes that. We work directly with vineyards, importers, sommeliers and venue owners to ticket their events in one place — so the next time you&apos;re looking for a vertical of Pinot or a Champagne vs. crémant blind, you find it the first try.</p>
        <p>Pick a city above to see what&apos;s pouring this month.</p>
      </>
    ),
  },
};

// ---------------------------------------------------------------------------
// Comedy — indigo / fuchsia
// ---------------------------------------------------------------------------
export const comedyConfig = {
  slug: 'comedy',
  label: 'Comedy',
  plural: 'Comedy Shows',
  organizerNoun: 'comedy show',
  Icon: Mic2,
  heroImage: 'https://images.unsplash.com/photo-1527224857830-43a7acc85260?fm=webp&q=70&w=1600',
  heroEyebrow: 'The Eventism Comedy Hub',
  heroTagline: { lead: 'Comedy that', highlight: 'lands', trail: '.' },
  heroSubtitle: {
    national: 'Headliner sets, open mics, sketch nights and one-off specials — every comedy club and pop-up room we cover, in one calendar.',
    city: ({ cityName }) => `Headliners, open mics and sketch nights across ${cityName}. Eventism keeps the late-show calendar in one place.`,
  },
  badges: [
    { icon: Mic2,  label: 'Stand-up sets' },
    { icon: Drama, label: 'Sketch nights' },
    { icon: Laugh, label: 'Open mics' },
  ],
  accent: {
    heroOverlay:    'bg-gradient-to-br from-[#1a0b3b]/95 via-[#0a0420]/90 to-[#1c0a2e]/95',
    titleGradient:  'from-fuchsia-300 via-indigo-300 to-fuchsia-200',
    btnGradient:    'bg-gradient-to-r from-fuchsia-500 to-indigo-500',
    btnShadow:      'shadow-fuchsia-500/20',
    btnOutline:     'border-fuchsia-400/40 hover:bg-fuchsia-500/10',
    ctaBg:          'bg-gradient-to-br from-indigo-950/30 via-background to-fuchsia-950/30',
    eyebrowColor:   'text-fuchsia-300',
    linkText:       'text-fuchsia-300',
    cardBorder:     'border-fuchsia-400/20',
    hoverBorder:    'hover:border-fuchsia-400/50',
    hoverBorderAlt: 'hover:border-indigo-400/50',
    hoverBg:        'hover:bg-fuchsia-500/5',
    badgeBorder:    'border-fuchsia-400/30',
    badgeText:      'text-fuchsia-200',
    badgeBg:        'bg-fuchsia-500/10',
    badgePrimary:   'bg-fuchsia-500/90',
    badgeSecondary: 'bg-indigo-500/90 text-white',
    cardImageBg:    'bg-gradient-to-br from-fuchsia-900/30 via-indigo-900/20 to-fuchsia-950/40',
    fallbackIcon:   'text-fuchsia-300/40',
    statIconA:      'text-indigo-300',
    statIconB:      'text-fuchsia-300',
    priceText:      'text-fuchsia-300',
    formActiveBorder: 'border-fuchsia-400 bg-fuchsia-500/10',
    formHoverBorder:  'hover:border-fuchsia-400/40',
  },
  seoTitle: ({ isCityMode, cityName }) => isCityMode
    ? `Comedy Shows in ${cityName} — Stand-up, Open Mics & Late Shows | Eventism`
    : `Comedy Shows — Stand-up, Open Mics & Touring Headliners | Eventism`,
  seoDescription: ({ isCityMode, cityName, eventCount }) => isCityMode
    ? `${eventCount || 'Curated'} comedy shows in ${cityName}: stand-up, sketch, open mics, late-night specials. Tickets in one tap on Eventism.`
    : `The Eventism Comedy Hub: every stand-up set, sketch night and touring headliner across the cities we cover. One calendar, one checkout.`,
  seoKeywords: ({ isCityMode, cityName }) => isCityMode
    ? [`comedy ${cityName}`, `${cityName} stand-up`, `${cityName} comedy clubs`, `${cityName} open mic`, `comedy shows ${cityName}`, `things to do tonight ${cityName} comedy`]
    : ['comedy shows', 'stand-up tickets', 'open mic nights', 'sketch comedy', 'comedy near me', 'late night comedy'],
  seoCopy: {
    cityTitle: ({ cityName }) => `${cityName}'s comedy scene, in one calendar`,
    nationalTitle: 'Why a comedy hub?',
    city: ({ cityName, city, slug }) => (
      <>
        <p>{cityName}&apos;s funniest rooms aren&apos;t always the obvious ones. The best sets happen at the pop-up basement showcase, the Tuesday open mic above a brewery, the surprise drop-in from a touring headliner on their way through.</p>
        <p>Eventism puts the entire {cityName} comedy lineup in one place. Filter by date, by club, by headliner — and check out in two taps.</p>
        <p>Bookmark <a href={`/${slug}/${city}`} className="text-fuchsia-300 hover:underline">/{slug}/{city}</a> — we add new shows daily.</p>
      </>
    ),
    national: () => (
      <>
        <p>Touring comics rotate through 200 cities a year. Local rooms add new shows weekly. The result: the best comedy night near you is almost always something you didn&apos;t hear about until it was sold out.</p>
        <p>Eventism fixes the discovery gap. We work directly with clubs, producers and the comics themselves to consolidate every show — touring or local, headlining or mic — into one calendar.</p>
        <p>Pick a city above to see who&apos;s on the mic this week.</p>
      </>
    ),
  },
};

// ---------------------------------------------------------------------------
// Brunch — orange / pink
// ---------------------------------------------------------------------------
export const brunchConfig = {
  slug: 'brunch',
  label: 'Brunch',
  plural: 'Brunch Events',
  organizerNoun: 'brunch event',
  Icon: Coffee,
  heroImage: 'https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?fm=webp&q=70&w=1600',
  heroEyebrow: 'The Eventism Brunch Hub',
  heroTagline: { lead: 'Brunch that', highlight: 'goes till 4', trail: '.' },
  heroSubtitle: {
    national: 'Bottomless mimosa parties, drag brunches, themed prix fixes and full-block brunch crawls — every weekend, every city.',
    city: ({ cityName }) => `Bottomless mimosas, drag brunches and themed prix fixes across ${cityName}. Reserve, RSVP or book a whole table.`,
  },
  badges: [
    { icon: Egg,      label: 'Bottomless mimosa' },
    { icon: Utensils, label: 'Drag brunch' },
    { icon: Coffee,   label: 'Brunch crawls' },
  ],
  accent: {
    heroOverlay:    'bg-gradient-to-br from-[#3b1a0d]/95 via-[#1a0a08]/90 to-[#2a140d]/95',
    titleGradient:  'from-orange-300 via-pink-300 to-amber-200',
    btnGradient:    'bg-gradient-to-r from-orange-500 to-pink-500',
    btnShadow:      'shadow-orange-500/20',
    btnOutline:     'border-orange-400/40 hover:bg-orange-500/10',
    ctaBg:          'bg-gradient-to-br from-orange-950/30 via-background to-pink-950/30',
    eyebrowColor:   'text-orange-300',
    linkText:       'text-pink-300',
    cardBorder:     'border-pink-400/20',
    hoverBorder:    'hover:border-pink-400/50',
    hoverBorderAlt: 'hover:border-orange-400/50',
    hoverBg:        'hover:bg-pink-500/5',
    badgeBorder:    'border-pink-400/30',
    badgeText:      'text-pink-200',
    badgeBg:        'bg-pink-500/10',
    badgePrimary:   'bg-pink-500/90',
    badgeSecondary: 'bg-orange-500/90 text-black',
    cardImageBg:    'bg-gradient-to-br from-orange-900/30 via-pink-900/20 to-orange-950/40',
    fallbackIcon:   'text-pink-300/40',
    statIconA:      'text-orange-300',
    statIconB:      'text-pink-300',
    priceText:      'text-orange-300',
    formActiveBorder: 'border-pink-400 bg-pink-500/10',
    formHoverBorder:  'hover:border-pink-400/40',
  },
  seoTitle: ({ isCityMode, cityName }) => isCityMode
    ? `Brunch Events in ${cityName} — Bottomless Mimosas, Drag Brunch & Crawls | Eventism`
    : `Brunch Events — Bottomless Mimosas, Drag Brunches & Crawls | Eventism`,
  seoDescription: ({ isCityMode, cityName, eventCount }) => isCityMode
    ? `${eventCount || 'Curated'} brunch events in ${cityName}: bottomless mimosa parties, drag brunches, themed prix fixes, brunch crawls. Reserve in one tap.`
    : `The Eventism Brunch Hub: bottomless mimosas, drag brunches, themed prix fixes and brunch crawls across every city.`,
  seoKeywords: ({ isCityMode, cityName }) => isCityMode
    ? [`brunch ${cityName}`, `${cityName} bottomless brunch`, `${cityName} drag brunch`, `${cityName} brunch crawl`, `Sunday brunch ${cityName}`, `mimosas ${cityName}`]
    : ['brunch events', 'bottomless brunch', 'drag brunch', 'brunch crawl', 'weekend brunch', 'mimosa brunch'],
  seoCopy: {
    cityTitle: ({ cityName }) => `Brunch culture in ${cityName}`,
    nationalTitle: 'Why a brunch hub?',
    city: ({ cityName, city, slug }) => (
      <>
        <p>{cityName} doesn&apos;t do quiet brunch. The best tables are reserved weeks out — the drag brunches, the bottomless rooftop spots, the once-a-month chef collabs.</p>
        <p>Eventism keeps the whole calendar in one place, with online reservations and group bookings built in. No more group-chat scrambles to find a spot for ten on a Saturday.</p>
        <p>Bookmark <a href={`/${slug}/${city}`} className="text-pink-300 hover:underline">/{slug}/{city}</a> — we add new brunches every week.</p>
      </>
    ),
    national: () => (
      <>
        <p>Brunch is the most over-Googled meal of the week — and the hardest to actually book. Every city has its own constellation of bottomless spots, drag rooms and crawls, but they rarely show up on the same map.</p>
        <p>Eventism builds that map. Curated, ticketed, reservation-ready.</p>
        <p>Pick a city above to see what&apos;s pouring mimosas this Sunday.</p>
      </>
    ),
  },
};

// ---------------------------------------------------------------------------
// Holiday — red / green (covers NYE, Halloween, Xmas)
// ---------------------------------------------------------------------------
export const holidayConfig = {
  slug: 'holiday',
  label: 'Holiday',
  plural: 'Holiday Events',
  organizerNoun: 'holiday event',
  Icon: Gift,
  heroImage: 'https://images.unsplash.com/photo-1543589077-47d81606c1bf?fm=webp&q=70&w=1600',
  heroEyebrow: 'The Eventism Holiday Hub',
  heroTagline: { lead: 'NYE. Halloween.', highlight: 'Christmas', trail: '. Sorted.' },
  heroSubtitle: {
    national: 'Every NYE countdown party, Halloween costume bash, Friendsgiving and Christmas market — across every city we cover.',
    city: ({ cityName }) => `NYE countdowns, Halloween bashes, Friendsgivings and Christmas markets across ${cityName}. Tickets and guest lists in one tap.`,
  },
  badges: [
    { icon: PartyPopper, label: 'NYE parties' },
    { icon: Drama,       label: 'Halloween bashes' },
    { icon: Snowflake,   label: 'Christmas events' },
  ],
  accent: {
    heroOverlay:    'bg-gradient-to-br from-[#3b0d0d]/95 via-[#0a1a0a]/90 to-[#3a1500]/95',
    titleGradient:  'from-emerald-300 via-amber-300 to-red-300',
    btnGradient:    'bg-gradient-to-r from-red-500 to-emerald-500',
    btnShadow:      'shadow-red-500/20',
    btnOutline:     'border-red-400/40 hover:bg-red-500/10',
    ctaBg:          'bg-gradient-to-br from-red-950/30 via-background to-emerald-950/30',
    eyebrowColor:   'text-amber-300',
    linkText:       'text-red-300',
    cardBorder:     'border-red-400/20',
    hoverBorder:    'hover:border-red-400/50',
    hoverBorderAlt: 'hover:border-emerald-400/50',
    hoverBg:        'hover:bg-red-500/5',
    badgeBorder:    'border-red-400/30',
    badgeText:      'text-red-200',
    badgeBg:        'bg-red-500/10',
    badgePrimary:   'bg-red-500/90',
    badgeSecondary: 'bg-emerald-500/90 text-black',
    cardImageBg:    'bg-gradient-to-br from-red-900/30 via-amber-900/20 to-emerald-950/40',
    fallbackIcon:   'text-red-300/40',
    statIconA:      'text-amber-300',
    statIconB:      'text-red-300',
    priceText:      'text-amber-300',
    formActiveBorder: 'border-red-400 bg-red-500/10',
    formHoverBorder:  'hover:border-red-400/40',
  },
  seoTitle: ({ isCityMode, cityName }) => isCityMode
    ? `Holiday Events in ${cityName} — NYE, Halloween & Christmas | Eventism`
    : `Holiday Events — NYE, Halloween, Christmas in Every City | Eventism`,
  seoDescription: ({ isCityMode, cityName, eventCount }) => isCityMode
    ? `${eventCount || 'Curated'} holiday events in ${cityName}: New Year's Eve parties, Halloween costume bashes, Friendsgivings, Christmas markets. Tickets in one tap.`
    : `The Eventism Holiday Hub: NYE countdown parties, Halloween bashes, Friendsgivings, Christmas markets — across every city we cover.`,
  seoKeywords: ({ isCityMode, cityName }) => isCityMode
    ? [`NYE ${cityName}`, `New Year's Eve ${cityName}`, `Halloween ${cityName}`, `Christmas events ${cityName}`, `${cityName} holiday parties`, `things to do New Years ${cityName}`]
    : ['NYE parties', 'New Year\'s Eve events', 'Halloween parties', 'Christmas events', 'holiday parties', 'Friendsgiving'],
  seoCopy: {
    cityTitle: ({ cityName }) => `${cityName}'s holiday season, on one page`,
    nationalTitle: 'Why a holiday hub?',
    city: ({ cityName, city, slug }) => (
      <>
        <p>The three biggest party nights of the year — NYE, Halloween, Christmas Eve — sell out faster than any others. {cityName}&apos;s best venues drop their tickets weeks in advance, and the best curated parties are usually invite-only until the last minute.</p>
        <p>Eventism collects every one in one place. NYE countdowns, Halloween costume contests, Friendsgivings and Christmas markets — filter by date, neighborhood or vibe, and check out in two taps.</p>
        <p>Bookmark <a href={`/${slug}/${city}`} className="text-red-300 hover:underline">/{slug}/{city}</a> — we add new events as the holidays approach.</p>
      </>
    ),
    national: () => (
      <>
        <p>Holiday party planning is brutal. Every venue books out months in advance, every group chat scrambles for tickets in week 3 of December, and half the best parties never even make Google.</p>
        <p>Eventism solves it. One hub, every holiday, every city — with the ticketing and group-checkout built right in.</p>
        <p>Pick a city above to start planning.</p>
      </>
    ),
  },
};


// ---------------------------------------------------------------------------
// NYE — midnight blue / champagne gold (high-intent / high-traffic)
// ---------------------------------------------------------------------------
export const nyeConfig = {
  slug: 'nye',
  label: "New Year's Eve",
  plural: "New Year's Eve Parties",
  organizerNoun: "New Year's Eve party",
  Icon: PartyPopper,
  heroImage: 'https://images.unsplash.com/photo-1546271876-af6caec5fae4?fm=webp&q=70&w=1600',
  heroEyebrow: 'The Eventism NYE Hub',
  heroTagline: { lead: 'Ring it in.', highlight: 'Anywhere but home', trail: '.' },
  heroSubtitle: {
    national: "Black-tie galas, rooftop countdowns, all-night warehouse parties and open-bar champagne pours — every NYE event in every city we cover.",
    city: ({ cityName }) => `Black-tie galas, rooftop countdowns and all-night parties across ${cityName}. The full NYE calendar, in one place.`,
  },
  badges: [
    { icon: PartyPopper, label: 'Midnight countdowns' },
    { icon: Star,        label: 'Black-tie galas' },
    { icon: Music2,      label: 'Rooftop parties' },
  ],
  accent: {
    heroOverlay:    'bg-gradient-to-br from-[#0a0a3b]/95 via-[#06061f]/90 to-[#1a1a3b]/95',
    titleGradient:  'from-yellow-200 via-amber-300 to-yellow-100',
    btnGradient:    'bg-gradient-to-r from-yellow-400 to-amber-500',
    btnShadow:      'shadow-amber-500/20',
    btnOutline:     'border-amber-400/40 hover:bg-amber-500/10',
    ctaBg:          'bg-gradient-to-br from-indigo-950/40 via-background to-amber-950/30',
    eyebrowColor:   'text-amber-300',
    linkText:       'text-amber-300',
    cardBorder:     'border-amber-400/20',
    hoverBorder:    'hover:border-amber-400/50',
    hoverBorderAlt: 'hover:border-indigo-400/50',
    hoverBg:        'hover:bg-amber-500/5',
    badgeBorder:    'border-amber-400/30',
    badgeText:      'text-amber-200',
    badgeBg:        'bg-amber-500/10',
    badgePrimary:   'bg-amber-500/90 text-black',
    badgeSecondary: 'bg-indigo-500/90',
    cardImageBg:    'bg-gradient-to-br from-indigo-900/30 via-amber-900/20 to-indigo-950/40',
    fallbackIcon:   'text-amber-300/40',
    statIconA:      'text-amber-300',
    statIconB:      'text-yellow-200',
    priceText:      'text-amber-300',
    formActiveBorder: 'border-amber-400 bg-amber-500/10',
    formHoverBorder:  'hover:border-amber-400/40',
  },
  seoTitle: ({ isCityMode, cityName }) => isCityMode
    ? `New Year's Eve in ${cityName} — Best NYE Parties, Galas & Countdowns | Eventism`
    : `New Year's Eve Parties — Best NYE Events in Every City | Eventism`,
  seoDescription: ({ isCityMode, cityName, eventCount }) => isCityMode
    ? `${eventCount || 'Curated'} New Year's Eve parties in ${cityName}: black-tie galas, rooftop countdowns, open-bar nights, all-night warehouse parties. Lock in tickets early.`
    : `The Eventism NYE Hub: black-tie galas, rooftop countdowns, open-bar parties and the best New Year's Eve events across every city we cover.`,
  seoKeywords: ({ isCityMode, cityName }) => isCityMode
    ? [`NYE ${cityName}`, `New Year's Eve ${cityName}`, `${cityName} NYE party`, `${cityName} New Year's Eve`, `NYE tickets ${cityName}`, `New Years party ${cityName}`, `things to do NYE ${cityName}`]
    : ["New Year's Eve parties", "NYE parties", "NYE 2026", "NYE tickets", "New Year's Eve events", "NYE black tie"],
  seoCopy: {
    cityTitle: ({ cityName }) => `${cityName}'s biggest night, sorted`,
    nationalTitle: 'Why a dedicated NYE hub?',
    city: ({ cityName, city, slug }) => (
      <>
        <p>New Year&apos;s Eve in {cityName} is the most over-planned and under-booked night of the year. The best black-tie galas, the best rooftop countdowns, the best open-bar parties — they all sell out by mid-December, but the calendars are scattered across a dozen Instagram pages.</p>
        <p>Eventism is the one calendar. Curated, ticketed, group-checkout ready. Bookmark <a href={`/${slug}/${city}`} className="text-amber-300 hover:underline">/{slug}/{city}</a> now and we&apos;ll add more as the year winds down.</p>
        <p>Pro tip: book in pairs. The best NYE tickets always sell faster than the same-night dinner reservations.</p>
      </>
    ),
    national: () => (
      <>
        <p>One night a year, every major city throws its best party of the year — and tickets vanish from public listings months before midnight. The result: most people end up paying triple at the door or settling for a sad bar Stout.</p>
        <p>Eventism fixes the planning problem. We work directly with promoters, venues and hospitality groups to consolidate every NYE party into one searchable hub.</p>
        <p>Pick a city above. Get a date plan that doesn&apos;t involve hours in a group chat.</p>
      </>
    ),
  },
};

// ---------------------------------------------------------------------------
// Halloween — orange / blood-red / charcoal (high-intent seasonal)
// ---------------------------------------------------------------------------
export const halloweenConfig = {
  slug: 'halloween',
  label: 'Halloween',
  plural: 'Halloween Events',
  organizerNoun: 'Halloween party',
  Icon: Ghost,
  heroImage: 'https://images.unsplash.com/photo-1509557965875-b88c97052f0e?fm=webp&q=70&w=1600',
  heroEyebrow: 'The Eventism Halloween Hub',
  heroTagline: { lead: 'Costumes. Open bars.', highlight: 'Unholy good time', trail: '.' },
  heroSubtitle: {
    national: 'Costume contests, haunted bars, monster mashes and burlesque ball after-dark — every Halloween event in every city we cover.',
    city: ({ cityName }) => `Costume contests, haunted bars and monster mashes across ${cityName}. The full Halloween calendar, in one place.`,
  },
  badges: [
    { icon: Skull, label: 'Costume contests' },
    { icon: Ghost, label: 'Haunted bars' },
    { icon: Flame, label: 'After-dark parties' },
  ],
  accent: {
    heroOverlay:    'bg-gradient-to-br from-[#3b1a00]/95 via-[#0a0506]/90 to-[#2a0a00]/95',
    titleGradient:  'from-orange-300 via-amber-400 to-orange-500',
    btnGradient:    'bg-gradient-to-r from-orange-500 to-red-600',
    btnShadow:      'shadow-orange-500/30',
    btnOutline:     'border-orange-400/40 hover:bg-orange-500/10',
    ctaBg:          'bg-gradient-to-br from-orange-950/40 via-background to-red-950/30',
    eyebrowColor:   'text-orange-300',
    linkText:       'text-orange-300',
    cardBorder:     'border-orange-400/20',
    hoverBorder:    'hover:border-orange-400/50',
    hoverBorderAlt: 'hover:border-red-400/50',
    hoverBg:        'hover:bg-orange-500/5',
    badgeBorder:    'border-orange-400/30',
    badgeText:      'text-orange-200',
    badgeBg:        'bg-orange-500/10',
    badgePrimary:   'bg-orange-500/90 text-black',
    badgeSecondary: 'bg-red-600/90',
    cardImageBg:    'bg-gradient-to-br from-orange-900/30 via-red-900/20 to-orange-950/50',
    fallbackIcon:   'text-orange-300/40',
    statIconA:      'text-orange-300',
    statIconB:      'text-red-400',
    priceText:      'text-orange-300',
    formActiveBorder: 'border-orange-400 bg-orange-500/10',
    formHoverBorder:  'hover:border-orange-400/40',
  },
  seoTitle: ({ isCityMode, cityName }) => isCityMode
    ? `Halloween in ${cityName} — Costume Parties, Haunted Bars & Monster Mashes | Eventism`
    : `Halloween Events — Best Costume Parties in Every City | Eventism`,
  seoDescription: ({ isCityMode, cityName, eventCount }) => isCityMode
    ? `${eventCount || 'Curated'} Halloween events in ${cityName}: costume contests, haunted bars, monster mashes, after-dark parties. The full Spooky Season calendar.`
    : `The Eventism Halloween Hub: costume contests, haunted bars, monster mashes and the best Halloween parties across every city we cover.`,
  seoKeywords: ({ isCityMode, cityName }) => isCityMode
    ? [`Halloween ${cityName}`, `${cityName} Halloween party`, `${cityName} costume contest`, `Halloween bars ${cityName}`, `${cityName} haunted`, `Halloween events ${cityName}`, `things to do Halloween ${cityName}`]
    : ['Halloween parties', 'Halloween events', 'costume contests', 'haunted bars', 'Halloween bar crawl', 'monster mash'],
  seoCopy: {
    cityTitle: ({ cityName }) => `Spooky Season, ${cityName} edition`,
    nationalTitle: 'Why a dedicated Halloween hub?',
    city: ({ cityName, city, slug }) => (
      <>
        <p>Halloween in {cityName} isn&apos;t one night — it&apos;s a three-weekend marathon. Costume contests at the lounges, haunted experiences in the warehouse district, themed parties at the rooftops, and the after-dark crawls that don&apos;t even start till midnight.</p>
        <p>Eventism puts the whole Spooky Season in one calendar. Filter by date, neighborhood or vibe. Tickets in two taps. Bookmark <a href={`/${slug}/${city}`} className="text-orange-300 hover:underline">/{slug}/{city}</a> — we add new events through October.</p>
        <p>Pro tip: costumes get judged. We tag every party that runs a contest so you know when to go all-in.</p>
      </>
    ),
    national: () => (
      <>
        <p>Halloween is the second biggest party night of the year — and the most underbooked. Most cities cram a year&apos;s worth of themed parties into three weekends, and most planners never find more than one or two of them.</p>
        <p>Eventism gathers them all. Costume contests with prizes. Haunted bar crawls. Burlesque balls. Lights-out warehouse raves. One hub, every city.</p>
        <p>Pick a city above. Plan a costume that wins.</p>
      </>
    ),
  },
};
